import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PetSprite } from './PetSprites';
import { Sparkles, Star, ArrowRight } from 'lucide-react';

const STAGE_NAMES = {
  1: { bm: 'Bayi', en: 'Baby' },
  2: { bm: 'Kanak-kanak', en: 'Child' },
  3: { bm: 'Remaja', en: 'Teen' },
  4: { bm: 'Dewasa', en: 'Adult' },
};

const PET_COLORS = {
  harimau: '#F97316',
  enggang: '#FBBF24',
  penyu: '#34D399',
};

export default function PetEvolutionPopup({ show, petType, petName, oldStage, newStage, onClose, isEN = false }) {
  if (!show || !petType) return null;

  const color = PET_COLORS[petType] || '#38bdf8';
  const oldName = STAGE_NAMES[oldStage] || { bm: '?', en: '?' };
  const newName = STAGE_NAMES[newStage] || { bm: '?', en: '?' };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="pet-evolution-popup"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(2,6,23,0.92)', backdropFilter: 'blur(12px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            onClick={e => e.stopPropagation()}
            style={{
              background: '#0f172a', borderRadius: 28, padding: '40px 32px',
              border: `2px solid ${color}40`, maxWidth: 360, width: '100%',
              textAlign: 'center', position: 'relative', overflow: 'hidden',
            }}
          >
            {/* Background glow */}
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 200, height: 200, borderRadius: '50%', background: color, opacity: 0.08, filter: 'blur(60px)' }} />

            {/* Sparkle particles */}
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0 }}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1, 0],
                  x: Math.cos(i * Math.PI / 4) * 80,
                  y: Math.sin(i * Math.PI / 4) * 80,
                }}
                transition={{ duration: 1.5, delay: 0.3 + i * 0.1, repeat: Infinity, repeatDelay: 1 }}
                style={{
                  position: 'absolute', top: '40%', left: '50%',
                  width: 6, height: 6, borderRadius: '50%',
                  background: color,
                }}
              />
            ))}

            {/* Title */}
            <motion.div
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Sparkles size={28} color={color} style={{ marginBottom: 8 }} />
              <h2 style={{ fontSize: 24, fontWeight: 800, color: '#f8fafc', margin: '0 0 4px', fontFamily: "'Outfit', sans-serif" }}>
                {isEN ? 'EVOLUTION!' : 'EVOLUSI!'}
              </h2>
              <p style={{ fontSize: 14, color: '#94a3b8', margin: 0 }}>
                {petName} {isEN ? 'has evolved!' : 'telah berevolusi!'}
              </p>
            </motion.div>

            {/* Evolution visual */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, margin: '24px 0' }}>
              <motion.div
                initial={{ opacity: 1 }} animate={{ opacity: 0.4 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <PetSprite petType={petType} stage={oldStage} size={60} mood="happy" animate={false} />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontWeight: 600 }}>
                  {isEN ? oldName.en : oldName.bm}
                </div>
              </motion.div>

              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.6, type: 'spring' }}
              >
                <ArrowRight size={24} color={color} />
              </motion.div>

              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.8, type: 'spring', stiffness: 200 }}
              >
                <PetSprite petType={petType} stage={newStage} size={80} mood="happy" animate={false} />
                <div style={{ fontSize: 13, color: color, marginTop: 4, fontWeight: 800 }}>
                  {isEN ? newName.en : newName.bm}
                </div>
              </motion.div>
            </div>

            {/* Stage indicator */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 20 }}>
              {[1, 2, 3, 4].map(s => (
                <div key={s} style={{
                  width: s <= newStage ? 24 : 16, height: 6, borderRadius: 100,
                  background: s <= newStage ? color : '#1e293b',
                  transition: 'all 0.3s',
                }} />
              ))}
            </div>

            {/* Close button */}
            <motion.button
              data-testid="evolution-close-btn"
              onClick={onClose}
              whileTap={{ scale: 0.95 }}
              style={{
                background: color, color: '#020617', border: 'none',
                borderRadius: 100, padding: '12px 32px', fontSize: 15, fontWeight: 700,
                cursor: 'pointer', fontFamily: "'Nunito', sans-serif",
              }}
            >
              {isEN ? 'Awesome!' : 'Hebat!'}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
